import React, {
  ExoticComponent,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import { Text, Title } from '@siftscience/focus-components/text'
import { Button, IconButton } from '@siftscience/focus-components/button'
import {
  AddNew,
  AtEmail,
  Calendar,
  CalendarWithDate,
  Checkmark,
  Close,
  FileText,
  NoIcon,
  ReturnLog,
  TextAa,
  TreeStructure,
  List
} from '@siftscience/focus-components/icons'
import { makeStyles } from '@material-ui/core/styles'
import { Popover } from '@siftscience/focus-components/popover'
import { TextInput } from '@siftscience/focus-components/input'
import Action from './action'
import Select, { DropdownItem } from '../select'
import { useTemplateContext } from '../context/template-context'
import { useDataFieldContext } from '../context/data-field-context'
import {
  DataField,
  DataFieldType,
  ProcessorFieldType,
  TemplateDTO
} from '../dto/template'
import { Modal } from '@siftscience/focus-components/modal'
import { EditableSchemaTable } from '../EditableSchemaTable'
import { initialSchema } from '../EditableSchemaTable/helpers'

const useStyles = makeStyles(() => ({
  actionBlock: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'flex-end',
    width: '100%',
    padding: '8px 16px',
    borderTop: '1px solid #E0E0E0'
  },
  actionButton: {
    display: 'flex',
    flexDirection: 'row'
  },
  addActionsBlock: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px'
  },
  addNewTemplateBtn: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'fit-content',
    gap: '8px',

    '& svg': {
      width: '16px',
      height: '16px'
    }
  },
  addNewSubtemplateInputWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'fit-content',
    justifyContent: 'space-between',
    gap: '8px'
  },
  icon: {
    width: '16px',
    height: '16px'
  },
  dataFieldItemWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    backgroundColor: '#FFF6D6',
    width: '100%',
    height: '100%',
    borderRadius: '4px',
    padding: '2px 8px',
    marginRight: '0 !important',

    '&.required': {
      backgroundColor: '#FFEDEC'
    }
  }
}))

interface AddRowProps {
  onAddSubtemplate: (subtemplateId: number) => void
  onAddDataField: (dataFieldId: number) => void
  onCreateDataField: (dataField: Partial<DataField>) => void
  template: TemplateDTO
}

const AddRow = ({
  onAddSubtemplate,
  onAddDataField,
  onCreateDataField,
  template
}: AddRowProps) => {
  const [isAddingNewSubtemplate, setIsAddingNewSubtemplate] = useState(false)
  const [openConfigurationModal, setOpenConfigurationModal] = useState(false)
  const [saveDataField, setSaveDataField] = useState<DataField>({} as DataField)
  const [isAddingNewDataField, setIsAddingNewDataField] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const { relevantTemplates, createTemplate } = useTemplateContext()
  const { dataFields, dataFieldsHash } = useDataFieldContext()
  const popoverOnClickRef = useRef(null)
  const classes = useStyles()
  const onAddNewSubtemplateClicked = () => {
    setIsAddingNewSubtemplate(true)
  }

  const onAddNewDataFieldClicked = () => {
    setIsAddingNewDataField(true)
  }

  const onSaveNewTemplate = () => {
    setIsAddingNewSubtemplate(false)
    createTemplate(template, { name: newTemplateName })
  }

  const onCancelCreatingTemplate = () => {
    setIsAddingNewSubtemplate(false)
    setNewTemplateName('')
  }

  const onCancelCreatingDataField = () => {
    setIsAddingNewDataField(false)
  }

  const preventDefaultBehaviour = event => {
    event.stopPropagation()
  }

  const onNewTemplateNameChange = event => {
    const value = event?.target?.value
    setNewTemplateName(value)
  }

  const subtemplatesHash = useMemo(() => {
    const hash = new Map()
    const subtemplates = relevantTemplates?.[template?.templateId] || []
    subtemplates.forEach(relevantSubtemplate =>
      hash.set(relevantSubtemplate.templateId, relevantSubtemplate)
    )
    return hash
  }, [relevantTemplates, template])

  const subtemplateOptions = useMemo(() => {
    return (relevantTemplates?.[template?.templateId] || []).map(option => ({
      name: option.displayName,
      id: option.templateId
    }))
  }, [relevantTemplates, template])

  const dataFieldOptions = useMemo(() => {
    return (
      dataFields.map(dataField => ({
        id: dataField.id,
        name: dataField.name
      })) || []
    )
  }, [dataFields])

  const renderSubtemplateItem = useCallback(
    (item: DropdownItem) => {
      const template = subtemplatesHash.get(item.id as number)
      let Icon: ExoticComponent<{
        children?: React.ReactNode
        color?: string
      }> = null
      if (template?.processorField?.data_type === ProcessorFieldType.STRING) {
        Icon = TextAa
      } else if (
        template?.processorField?.data_type === ProcessorFieldType.PDF
      ) {
        Icon = FileText
      } else if (
        template?.processorField?.data_type === ProcessorFieldType.IMAGE
      ) {
        Icon = NoIcon
      }

      return (
        <>
          {Icon && <Icon color="#B3B3B3" />}
          <Text size="small">{item.name}</Text>
        </>
      )
    },
    [subtemplatesHash]
  )

  const renderDataFieldItem = useCallback(
    (item: DropdownItem) => {
      const dataField = dataFieldsHash.get(item.id as number)
      let Icon: ExoticComponent<{
        children?: React.ReactNode
        className?: string
        color?: string
      }> = Fragment
      if (dataField?.dataType === DataFieldType.TEXT) {
        Icon = TextAa
      } else if (dataField?.dataType === DataFieldType.IMAGE) {
        Icon = NoIcon
      } else if (dataField?.dataType === DataFieldType.DATE) {
        Icon = Calendar
      } else if (dataField?.dataType === DataFieldType.DATETIME) {
        Icon = CalendarWithDate
      } else if (dataField?.dataType === DataFieldType.BOOLEAN) {
        Icon = TreeStructure
      } else if (dataField?.dataType === DataFieldType.ARRAY) {
        Icon = List
      }

      return (
        <div
          className={`${classes.dataFieldItemWrapper} ${
            dataField?.required ? 'required' : ''
          }`}
        >
          <Icon color="#B3B3B3" className={classes.icon} />
          <Text size="small">{item.name}</Text>
        </div>
      )
    },
    [dataFieldsHash]
  )

  const onSaveSchema = data => {
    onCreateDataField({ ...saveDataField, schema: data })
  }

  const subtemplateDropdownFooter = useMemo(
    () =>
      isAddingNewSubtemplate ? (
        <div className={classes.addNewSubtemplateInputWrapper}>
          <TextInput
            value={newTemplateName}
            onChange={onNewTemplateNameChange}
            onKeyUp={preventDefaultBehaviour}
            onKeyDown={preventDefaultBehaviour}
          />
          <IconButton onClick={onSaveNewTemplate} variant="secondary" lined>
            <Checkmark color="#0AA636" />
          </IconButton>
          <IconButton
            onClick={onCancelCreatingTemplate}
            variant="secondary"
            lined
          >
            <Close color="#EB3B2E" />
          </IconButton>
        </div>
      ) : (
        <Button variant="primary-ghost" onClick={onAddNewSubtemplateClicked}>
          <div className={classes.addNewTemplateBtn}>
            <AddNew />
            <Title size="xsmall">Add New Template</Title>
          </div>
        </Button>
      ),
    [
      isAddingNewSubtemplate,
      newTemplateName,
      onNewTemplateNameChange,
      preventDefaultBehaviour,
      onSaveNewTemplate,
      onCancelCreatingTemplate,
      onAddNewSubtemplateClicked
    ]
  )

  const dataFieldDropdownFooter = (
    <Button variant="primary-ghost" onClick={onAddNewDataFieldClicked}>
      <div className={classes.addNewTemplateBtn}>
        <AddNew />
        <Title size="xsmall">Add New Field</Title>
      </div>
    </Button>
  )

  return (
    <div className={classes.actionBlock}>
      <Title size="xsmall">Add</Title>
      <div className={classes.addActionsBlock}>
        <Popover
          placement="top"
          content={() => (
            <Action
              isNewDataField={true}
              setOpenConfigurationModal={setOpenConfigurationModal}
              defaultDataType={DataFieldType.IMAGE}
              dataFieldOptions={[DataFieldType.IMAGE]}
              onCancel={event => {
                if (popoverOnClickRef.current) {
                  popoverOnClickRef.current(event)
                }
              }}
              onSaveDataField={setSaveDataField}
              onSave={dataField => {
                onCreateDataField(dataField)
              }}
            />
          )}
        >
          {props => {
            popoverOnClickRef.current = props.onClick
            return (
              <Button
                variant="secondary"
                className={classes.actionButton}
                lined
                {...props}
              >
                <NoIcon />
                <Title size="xsmall">Image</Title>
              </Button>
            )
          }}
        </Popover>
        <Select
          items={subtemplateOptions}
          onSelect={item => onAddSubtemplate(item.id as number)}
          renderItem={renderSubtemplateItem}
          onOpenChange={onCancelCreatingTemplate}
          renderTrigger={toggleButtonProps => (
            <Button
              variant="secondary"
              className={classes.actionButton}
              lined
              {...toggleButtonProps}
            >
              <ReturnLog />
              <Title size="xsmall">Subtemplate</Title>
            </Button>
          )}
          footerContent={subtemplateDropdownFooter}
        />
        {isAddingNewDataField ? (
          <Popover
            placement="top"
            defaultOpen
            content={() => (
              <Action
                isNewDataField={true}
                setOpenConfigurationModal={setOpenConfigurationModal}
                defaultDataType={DataFieldType.TEXT}
                onCancel={event => {
                  if (popoverOnClickRef.current) {
                    popoverOnClickRef.current(event)
                  }
                  setIsAddingNewDataField(false)
                }}
                onSaveDataField={setSaveDataField}
                onSave={dataField => {
                  onCreateDataField(dataField)
                  setIsAddingNewDataField(false)
                }}
              />
            )}
          >
            {props => {
              popoverOnClickRef.current = props.onClick
              return (
                <Button
                  variant="secondary"
                  className={classes.actionButton}
                  lined
                  {...props}
                  onClick={event => {
                    props.onClick(event)
                    setIsAddingNewDataField(false)
                  }}
                >
                  <AtEmail />
                  <Title size="xsmall">Data Field</Title>
                </Button>
              )
            }}
          </Popover>
        ) : (
          <Select
            items={dataFieldOptions}
            onSelect={item => onAddDataField(item.id as number)}
            renderItem={renderDataFieldItem}
            onOpenChange={onCancelCreatingDataField}
            renderTrigger={toggleButtonProps => (
              <Button
                variant="secondary"
                className={classes.actionButton}
                lined
                {...toggleButtonProps}
              >
                <AtEmail />
                <Title size="xsmall">Data Field</Title>
              </Button>
            )}
            footerContent={dataFieldDropdownFooter}
          />
        )}
      </div>
    </div>
  )
}

export default AddRow
